import type { IJobApplicationStore } from 'src/stores/JobApplicationStore';
import type { Language } from '@seek/melways-sites';
import { useStores } from 'src/utils/hooks/useStores';
import {
  type FiltersInteractedAttributes,
  trackFiltersInteracted,
  trackMustHaveFilterInteracted,
  trackParentFilterInteractedOff,
  trackParentFilterInteractedOn,
} from 'src/utils/events';
import { sendHotjarTag } from 'src/utils/hotjar';
import { HOTJAR_FILTERS_INTERACTED } from 'src/constants/hotjar';
import {
  TOGGLE_STATE_ON,
  TOGGLE_STATE_OFF,
  MUST_HAVE_TOGGLE_TYPE,
} from 'src/constants/toggle';
import type {
  EVENT_NAME_CLEAR_FILTERS_BUTTON_PRESSED,
  EVENT_NAME_FILTERS_INTERACTED,
} from 'src/constants/pageTrackingData';
import type { SearchFilters } from 'src/stores/JobApplicationStore/types';
import {
  buildFiltersData,
  type FiltersEventCaptureData,
} from 'src/services/jobApplicationService/buildCaptureEventData';
import { useState } from 'react';
import type { LocationFilterSuggestionItem } from 'src/App/AppPageBlock/LocationFilter';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import type { NationalityFacet } from 'src/App/AppPageBlock/NationalityFilter/NationalityFilter';
import { useExperimentConfig } from 'src/App/CandidateManagementPage/ExperimentContext';

export interface TrackingData {
  eventName:
    | typeof EVENT_NAME_FILTERS_INTERACTED
    | typeof EVENT_NAME_CLEAR_FILTERS_BUTTON_PRESSED;
  actionOrigin:
    | 'candidate listing - filter section'
    | 'candidate listing - centre cta with must have'
    | 'candidate listing - centre cta without must have';
}

export interface Filters {
  selectedFilterCount: number;
  selectedHomeLocations: LocationFilterSuggestionItem[];
  selectedNationalities: NationalityFacet[];
  searchFilters: SearchFilters;
  filtersEventCaptureData: FiltersEventCaptureData;
}

export interface FilterFns {
  setSelectedHomeLocations: (newValue: LocationFilterSuggestionItem[]) => void;
  setSelectedNationalities: (newValue: NationalityFacet[]) => void;
  onMustHaveFilterToggled: () => void;
  onParentFilterSelected: (isSelected: boolean) => void;
  onFilterSelected: () => void;
  resetFilters: (trackingData: TrackingData) => void;
  clearFilters: () => void;
}

export const useFilters = ({
  language,
}: {
  language: Language;
}): { filters: Filters; filterFns: FilterFns } => {
  const {
    jobApplicationStore,
    jobStore: {
      job: {
        jobInformation: { jobId },
      },
    },
    seekAdvertiserId,
    userStore: {
      user: { userId },
    },
  } = useStores();

  const [, setQueryParams] = useQueryParamsStore();

  const { filtersStore, loadJobApplications } =
    jobApplicationStore as IJobApplicationStore;
  const { isExperimentEnabled } = useExperimentConfig();

  const [selectedHomeLocations, setSelectedHomeLocations] = useState<
    LocationFilterSuggestionItem[]
  >([]);
  const [selectedNationalities, setSelectedNationalities] = useState<
    NationalityFacet[]
  >([]);

  const selectedFilterCount =
    filtersStore.selectedRrFilters.length +
    selectedHomeLocations.length +
    selectedNationalities.length +
    (jobApplicationStore.hasSearchText ? 1 : 0);

  const searchFilters: SearchFilters = {
    rrGlobalFilters: filtersStore.selectedRrFilters,
    homeLocationIds: selectedHomeLocations.map(({ id }) => id),
    nationalityRightToWorkIds: selectedNationalities.map(({ id }) => id),
  };

  const filtersEventCaptureData = buildFiltersData({
    jobApplicationStore,
    filtersStore,
    homeLocationIds: searchFilters.homeLocationIds ?? [],
    nationalityIds: searchFilters.nationalityRightToWorkIds ?? [],
  });

  const getTrackingExperiments = ():
    | Array<{
        num: number;
        id: string;
        variant: string;
      }>
    | undefined => {
    const experiments = [];
    if (isExperimentEnabled('growth-placement-passive-flow')) {
      experiments.push({
        num: 3,
        id: 'cm_growth_placement',
        variant: 'passive',
      });
    }

    if (isExperimentEnabled('growth-placement-assertive-flow-10-days')) {
      experiments.push({
        num: 3,
        id: 'cm_growth_placement',
        variant: 'assertive-10-days',
      });
    }

    if (isExperimentEnabled('growth-placement-assertive-flow-25-days')) {
      experiments.push({
        num: 3,
        id: 'cm_growth_placement',
        variant: 'assertive-25-days',
      });
    }

    if (experiments.length > 0) {
      return experiments;
    }

    return undefined;
  };

  // FIXME: We must directly reach into filtersStore for data in this function, otherwise we'd get the state from the previous render
  const resetFilters = (trackingData: TrackingData) => {
    if (selectedFilterCount > 0) {
      setSelectedHomeLocations([]);
      setSelectedNationalities([]);
      filtersStore.clearFilters();
      setQueryParams.navigateToSearch('');
      jobApplicationStore.clearAllSelected();
      trackFiltersEvent(trackingData);
      loadJobApplications({
        language,
        searchText: '',
        filters: {
          rrGlobalFilters: filtersStore.selectedRrFilters,
          homeLocationIds: [],
        },
        experiments: getTrackingExperiments(),
      });
    }
  };

  const trackingAttributes = {
    seekAdvertiserId,
    seekListingId: jobId!,
  };

  // FIXME: We must directly reach into filtersStore for data in this function, otherwise we'd get the state from the previous render
  const trackFiltersEvent = ({ eventName, actionOrigin }: TrackingData) => {
    if (!userId) {
      return;
    }

    const attributes: FiltersInteractedAttributes = {
      ...trackingAttributes,
      isCandidateListFiltered: filtersStore.hasSelectedFilter,
      userId,
    };
    if (actionOrigin) {
      attributes.actionOrigin = actionOrigin;
    }

    trackFiltersInteracted(eventName, attributes);
    sendHotjarTag(HOTJAR_FILTERS_INTERACTED);
  };

  // FIXME: We must directly reach into filtersStore for data in this function, otherwise we'd get the state from the previous render
  const trackParentFilterEvent = (isSelected: boolean) => {
    if (!userId) {
      return;
    }
    sendHotjarTag(HOTJAR_FILTERS_INTERACTED);
    if (isSelected) {
      trackParentFilterInteractedOn({
        ...trackingAttributes,
        isCandidateListFiltered: filtersStore.hasSelectedFilter,
        userId,
      });
      return;
    }
    trackParentFilterInteractedOff({
      ...trackingAttributes,
      isCandidateListFiltered: filtersStore.hasSelectedFilter,
      userId,
    });
  };

  // FIXME: We must directly reach into filtersStore for data in this function, otherwise we'd get the state from the previous render
  const trackMustHaveFilterEvent = () => {
    if (!userId) {
      return;
    }
    sendHotjarTag(HOTJAR_FILTERS_INTERACTED);
    trackMustHaveFilterInteracted({
      ...trackingAttributes,
      isCandidateListFiltered: filtersStore.hasSelectedFilter,
      userId,
      toggleState: filtersStore.isMustHaveToggleSelected
        ? TOGGLE_STATE_ON
        : TOGGLE_STATE_OFF,
      toggleType: MUST_HAVE_TOGGLE_TYPE,
    });
  };

  // FIXME: We must directly reach into filtersStore for data in this function, otherwise we'd get the state from the previous render
  const onFilterSelected = () => {
    jobApplicationStore.clearAllSelected();
    trackFiltersEvent({
      eventName: 'filters_pressed',
      actionOrigin: 'candidate listing - filter section',
    });
    loadJobApplications({
      language,
      filters: {
        rrGlobalFilters: filtersStore.selectedRrFilters,
        homeLocationIds: searchFilters.homeLocationIds,
        nationalityRightToWorkIds: searchFilters.nationalityRightToWorkIds,
      },
      experiments: getTrackingExperiments(),
    });
  };

  // FIXME: We must directly reach into filtersStore for data in this function, otherwise we'd get the state from the previous render
  const onParentFilterSelected = (isSelected: boolean): void => {
    trackParentFilterEvent(isSelected);
    loadJobApplications({
      language,
      filters: {
        rrGlobalFilters: filtersStore.selectedRrFilters,
        homeLocationIds: searchFilters.homeLocationIds,
        nationalityRightToWorkIds: searchFilters.nationalityRightToWorkIds,
      },
      experiments: getTrackingExperiments(),
    });
  };

  return {
    filters: {
      selectedHomeLocations,
      selectedFilterCount,
      searchFilters,
      filtersEventCaptureData,
      selectedNationalities,
    },
    filterFns: {
      setSelectedNationalities,
      setSelectedHomeLocations,
      onMustHaveFilterToggled: () => {
        filtersStore.toggleMustHaveFilter();
        trackMustHaveFilterEvent();
        onFilterSelected();
      },
      onParentFilterSelected,
      onFilterSelected,
      resetFilters,
      clearFilters: () => {
        setSelectedNationalities([]);
        setSelectedHomeLocations([]);
        filtersStore.clearFilters();
      },
    },
  };
};
